import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Users,
  UserCheck,
  Truck,
  Briefcase,
  Search,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * All Users Page - Unified view of Staff, Applicants, and Drivers
 * Routes: /admin/users
 * 
 * Supports scope parameter:
 * - scope=all (default): Shows all users including public applications
 * - scope=system: Shows only users with accounts (excludes public applications)
 */
const AllUsersPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data state
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({ staff: 0, applicants: 0, drivers: 0, total: 0, system_total: 0 });
  const [sources, setSources] = useState({});
  const [pagination, setPagination] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state - including scope from URL
  const [scopeFilter, setScopeFilter] = useState(searchParams.get('scope') || 'all');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || 'all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [programFilter, setProgramFilter] = useState(searchParams.get('program') || 'all');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  
  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Sync scope from URL params on mount
  useEffect(() => {
    const urlScope = searchParams.get('scope');
    if (urlScope && urlScope !== scopeFilter) {
      setScopeFilter(urlScope);
    }
  }, [searchParams]);

  const fetchUsers = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();
      if (scopeFilter !== 'all') params.append('scope', scopeFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (programFilter !== 'all') params.append('program', programFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);

      const response = await fetch(
        `${API_URL}/api/admin/all-users?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      // Handle both old (users) and new (items) response formats
      setUsers(data.items || data.users || []);
      setCounts(data.counts || { staff: 0, applicants: 0, drivers: 0, total: 0, system_total: 0 });
      setSources(data.sources || {});
      setPagination(data.pagination || null);
      setDebugInfo(data.debug || null);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError(`Failed to load users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [scopeFilter, categoryFilter, statusFilter, programFilter, debouncedSearch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (scopeFilter !== 'all') params.set('scope', scopeFilter);
    if (categoryFilter !== 'all') params.set('category', categoryFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (programFilter !== 'all') params.set('program', programFilter);
    if (searchTerm) params.set('search', searchTerm);
    setSearchParams(params);
  }, [scopeFilter, categoryFilter, statusFilter, programFilter, searchTerm, setSearchParams]);

  // Category badge helper
  const getCategoryBadge = (category) => {
    const config = {
      STAFF: { color: 'bg-purple-100 text-purple-800', icon: Briefcase },
      APPLICANT: { color: 'bg-blue-100 text-blue-800', icon: UserCheck },
      DRIVER: { color: 'bg-green-100 text-green-800', icon: Truck }
    };
    const { color, icon: Icon } = config[category] || { color: 'bg-gray-100 text-gray-800', icon: Users };
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {category}
      </Badge>
    );
  };

  // Status badge helper
  const getStatusBadge = (status, isActive) => {
    if (!isActive || status === 'removed' || status === 'archived') {
      return <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>;
    }
    
    const statusMap = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      new_application: { color: 'bg-blue-100 text-blue-800', label: 'New' },
      application_submitted: { color: 'bg-blue-100 text-blue-800', label: 'Submitted' },
      application_reviewed: { color: 'bg-indigo-100 text-indigo-800', label: 'Reviewed' },
      application_approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
      application_rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
      docs_submitted: { color: 'bg-orange-100 text-orange-800', label: 'Docs Submitted' },
      docs_pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Docs Pending' },
      onboarding_in_progress: { color: 'bg-cyan-100 text-cyan-800', label: 'Onboarding' },
      suspended: { color: 'bg-red-100 text-red-800', label: 'Suspended' }
    };
    
    const config = statusMap[status] || { color: 'bg-gray-100 text-gray-800', label: status || 'Unknown' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  // Role badge helper
  const getRoleBadge = (role) => {
    const roleMap = {
      super_admin: { color: 'bg-purple-100 text-purple-800', label: 'Super Admin' },
      ops_admin: { color: 'bg-blue-100 text-blue-800', label: 'Ops Admin' },
      admin: { color: 'bg-indigo-100 text-indigo-800', label: 'Admin' },
      driver: { color: 'bg-green-100 text-green-800', label: 'Driver' },
      applicant: { color: 'bg-gray-100 text-gray-800', label: 'Applicant' }
    };
    const config = roleMap[role] || { color: 'bg-gray-100 text-gray-800', label: role || '-' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  // Check for source errors
  const hasSourceErrors = Object.values(sources).some(s => s !== 'success' && !s.includes('skipped'));
  
  // Determine page title based on scope
  const pageTitle = scopeFilter === 'system' ? 'System Users' : 'All Records';
  const pageSubtitle = scopeFilter === 'system' 
    ? 'Users with accounts (Staff + Applicants + Drivers)' 
    : 'All records including public applications';

  if (loading && users.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
            <p className="text-sm text-gray-500 mt-1">{pageSubtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Scope Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setScopeFilter('all')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  scopeFilter === 'all' 
                    ? 'bg-white shadow text-gray-900 font-medium' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All Records ({counts.total})
              </button>
              <button
                onClick={() => setScopeFilter('system')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  scopeFilter === 'system' 
                    ? 'bg-white shadow text-gray-900 font-medium' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                System Only ({counts.system_total})
              </button>
            </div>
            <Button onClick={fetchUsers} variant="outline" className="flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
            <Button onClick={fetchUsers} size="sm" variant="outline">Retry</Button>
          </div>
        )}

        {/* Source Warning */}
        {hasSourceErrors && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <div>
                <span className="text-yellow-800 font-medium">Partial data loaded</span>
                <p className="text-yellow-700 text-sm mt-1">
                  Some data sources failed: 
                  {Object.entries(sources).filter(([_, v]) => v !== 'success').map(([k, v]) => (
                    <span key={k} className="ml-2">{k}: {v}</span>
                  ))}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Count Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className={`cursor-pointer transition-all ${categoryFilter === 'all' ? 'ring-2 ring-blue-500' : 'hover:shadow-md'}`}
            onClick={() => setCategoryFilter('all')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold">{counts.total}</div>
                  <div className="text-sm text-gray-500">Total Users</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${categoryFilter === 'staff' ? 'ring-2 ring-purple-500' : 'hover:shadow-md'}`}
            onClick={() => setCategoryFilter('staff')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Briefcase className="w-8 h-8 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold">{counts.staff}</div>
                  <div className="text-sm text-gray-500">Staff</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${categoryFilter === 'applicant' ? 'ring-2 ring-blue-500' : 'hover:shadow-md'}`}
            onClick={() => setCategoryFilter('applicant')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <UserCheck className="w-8 h-8 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold">{counts.applicants}</div>
                  <div className="text-sm text-gray-500">Applicants</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${categoryFilter === 'driver' ? 'ring-2 ring-green-500' : 'hover:shadow-md'}`}
            onClick={() => setCategoryFilter('driver')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Truck className="w-8 h-8 text-green-600" />
                <div>
                  <div className="text-2xl font-bold">{counts.drivers}</div>
                  <div className="text-sm text-gray-500">Drivers</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="users-search-input"
                />
              </div>

              {/* Category Filter */}
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="category-filter"
              >
                <option value="all">All Categories</option>
                <option value="staff">Staff Only</option>
                <option value="applicant">Applicants Only</option>
                <option value="driver">Drivers Only</option>
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="status-filter"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="suspended">Suspended</option>
              </select>

              {/* Program Filter */}
              <select
                value={programFilter}
                onChange={(e) => setProgramFilter(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="program-filter"
              >
                <option value="all">All Programs</option>
                <option value="dispatch">Dispatch-Only</option>
                <option value="lease">Lease-On</option>
                <option value="company_driver">Company Driver</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardContent className="pt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Email</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Category</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Program</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      {loading ? 'Loading...' : 'No users found matching filters'}
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.source_id || u.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{u.name}</div>
                        {u.phone && <div className="text-xs text-gray-500">{u.phone}</div>}
                      </td>
                      <td className="py-3 px-4 text-gray-600">{u.email}</td>
                      <td className="py-3 px-4">{getCategoryBadge(u.category)}</td>
                      <td className="py-3 px-4">{getRoleBadge(u.role)}</td>
                      <td className="py-3 px-4">{getStatusBadge(u.status, u.is_active)}</td>
                      <td className="py-3 px-4 text-gray-600">{u.program_type || '-'}</td>
                      <td className="py-3 px-4">
                        <Link 
                          to={u.detail_route}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            
            {/* Footer */}
            <div className="mt-4 pt-4 border-t flex flex-col gap-2">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>
                  Showing {users.length} of {pagination?.total_count || counts.total} total users
                  {pagination?.has_more && <span className="ml-2 text-blue-600">(more available)</span>}
                </span>
                <span className="text-xs">Build: {window.__BLUELOCK_BUILD_ID__ || 'N/A'}</span>
              </div>
              
              {/* Debug footer for super_admin */}
              {user?.role === 'super_admin' && (
                <div className="text-xs bg-gray-100 rounded p-2 font-mono">
                  <div className="font-semibold text-gray-600 mb-1">Debug Info (super_admin only):</div>
                  <div>
                    staff={counts.staff} applicants={counts.applicants} drivers={counts.drivers} 
                    {counts.public_apps > 0 && ` public_apps=${counts.public_apps}`} total={counts.total}
                  </div>
                  <div className="text-gray-400">
                    Sources: {Object.entries(sources).map(([k, v]) => `${k}=${v}`).join(' | ')}
                  </div>
                  {debugInfo && (
                    <div className="text-gray-400">
                      Endpoint: {debugInfo.endpoint}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AllUsersPage;
